/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * One body scroll lock for every modal, drawer and wall on the site.
 *
 * Until 2026-10-01 each overlay wrote `document.body.style.overflow` itself
 * and restored whatever it had saved. Five independent copies of that cannot
 * agree: the smart-charger info modal hands over to the MID checker, the
 * cookie wall can sit under either, and whichever lock released LAST wrote
 * back the value it had seen when it opened — `'hidden'` when another overlay
 * was already up — so the page stayed locked after everything had closed.
 * A reference count makes release order irrelevant: the page unlocks when
 * the last holder lets go, and never before.
 *
 * `overscroll-behavior: none` on <html> while locked is what stops a touch
 * scroll on iOS from chaining out of a modal's own scroller into the page
 * behind it (`overflow: hidden` alone never fully did there). The overlay
 * backdrops carry `touch-action: none` for the same reason.
 */

let holders = 0;
let savedBodyOverflow = '';
let savedHtmlOverscroll = '';

/**
 * Lock the page behind an overlay. Returns the release function; calling it
 * more than once is harmless. SSR-safe: a no-op without a document.
 */
export function lockPageScroll(): () => void {
  if (typeof document === 'undefined') return () => {};
  if (holders === 0) {
    const body = document.body;
    const root = document.documentElement;
    savedBodyOverflow = body.style.overflow;
    savedHtmlOverscroll = root.style.overscrollBehavior;
    body.style.overflow = 'hidden';
    root.style.overscrollBehavior = 'none';
  }
  holders += 1;
  let released = false;
  return () => {
    if (released) return;
    released = true;
    holders = Math.max(0, holders - 1);
    if (holders === 0) {
      document.body.style.overflow = savedBodyOverflow;
      document.documentElement.style.overscrollBehavior = savedHtmlOverscroll;
    }
  };
}

/** How many overlays currently hold the lock (tests + diagnostics). */
export function pageScrollLockCount(): number {
  return holders;
}
